import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Empty } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Inbox, Loader2, UserPlus, Mail, Phone, CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { new: "#F5A623", contacted: "#002EA6", qualified: "#8b5cf6", converted: "#10B981", archived: "#71717A" };
const TABS = ["all", "new", "contacted", "qualified", "converted"];
const STATUSES = ["new", "contacted", "qualified", "converted", "archived"];

export default function AdminLeads() {
  const [tab, setTab] = useState("all");
  const [leads, setLeads] = useState(null);
  const [active, setActive] = useState(null);
  const [inviting, setInviting] = useState(false);

  const load = () => {
    setLeads(null);
    api.get("/leads", { params: tab === "all" ? {} : { status: tab } }).then(({ data }) => setLeads(data));
  };
  useEffect(() => { load(); }, [tab]);

  const setStatus = async (id, status) => {
    const { data } = await api.patch(`/leads/${id}`, { status });
    setActive(data); load();
  };

  const invite = async () => {
    setInviting(true);
    try {
      await api.post(`/leads/${active.id}/invite`, { origin_url: window.location.origin });
      toast.success("Client invited — workspace created & email sent");
      const { data } = await api.get(`/leads/${active.id}`);
      setActive(data); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setInviting(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete the lead from ${active.company || "this company"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/leads/${active.id}`);
      toast.success("Lead deleted");
      setActive(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const Field = ({ label, value }) => (
    <div><p className="overline text-[0.55rem]">{label}</p><p className="text-sm mt-0.5">{value || "—"}</p></div>
  );

  return (
    <div>
      <PageHeader overline="Inbound search requests" title="Leads" />
      <div className="px-8 border-b border-black/10 flex gap-6 bg-white sticky top-16 z-10">
        {TABS.map((t) => (
          <button key={t} data-testid={`leadtab-${t}`} onClick={() => setTab(t)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px capitalize transition-colors ${tab === t ? "border-primary text-[#09090B]" : "border-transparent text-muted-foreground hover:text-[#09090B]"}`}>
            {t}
          </button>
        ))}
      </div>
      <div className="p-8">
        {!leads && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
        {leads && leads.length === 0 && <Empty icon={Inbox} title="No leads here" hint="Search requests from the For Clients page land in this inbox." />}
        {leads && leads.length > 0 && (
          <div className="space-y-px bg-black/10 border border-black/10">
            {leads.map((l) => (
              <button key={l.id} onClick={() => setActive(l)} data-testid={`lead-${l.id}`}
                className="w-full bg-white p-5 flex items-center justify-between hover:bg-[#F1F1F3] transition-colors text-left">
                <div>
                  <p className="font-medium">{l.company} <span className="text-muted-foreground font-normal">· {l.contact_name}</span></p>
                  <p className="overline text-[0.55rem] mt-1">{(l.roles || []).map((r) => r.title).filter(Boolean).join(", ") || "—"} · {l.engagement_type} · {(l.timeline || "").replace(/_/g, " ")}</p>
                </div>
                <span className="flex items-center gap-1.5 text-xs capitalize"><span className="h-1.5 w-1.5 rounded-full" style={{ background: STATUS_COLOR[l.status] || "#71717A" }} />{l.status}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-white border-black/10 rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          {active && (
            <>
              <DialogHeader><DialogTitle className="font-heading">{active.company}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Contact" value={active.contact_name} />
                  <Field label="Engagement" value={active.engagement_type} />
                  <div><p className="overline text-[0.55rem]">Email</p><a href={`mailto:${active.email}`} className="text-sm text-primary hover:underline flex items-center gap-1 mt-0.5"><Mail className="h-3 w-3" />{active.email}</a></div>
                  <div><p className="overline text-[0.55rem]">Phone</p><p className="text-sm mt-0.5 flex items-center gap-1">{active.phone ? <><Phone className="h-3 w-3" />{active.phone}</> : "—"}</p></div>
                  <div className="col-span-2"><p className="overline text-[0.55rem]">Roles & seniority</p>
                    <div className="mt-1 space-y-1">
                      {(active.roles || []).length ? (active.roles || []).map((r, i) => (
                        <div key={i} className="flex items-center justify-between text-sm border-b border-black/5 pb-1">
                          <span>{r.title || "—"}</span>
                          <span className="text-xs text-muted-foreground capitalize">{(r.seniority || "").replace(/_/g, " ")}</span>
                        </div>
                      )) : <p className="text-sm">—</p>}
                    </div>
                  </div>
                  <Field label="Timeline" value={(active.timeline || "").replace(/_/g, " ")} />
                  <Field label="Budget" value={active.budget} />
                </div>
                {active.message && <div className="border border-black/10 bg-[#F4F4F5] p-3"><p className="overline text-[0.55rem] mb-1">Notes</p><p className="text-sm">{active.message}</p></div>}
                <div className="flex items-center gap-3 pt-2 border-t border-black/10">
                  <div className="flex-1">
                    <p className="overline text-[0.55rem] mb-1">Status</p>
                    <Select value={active.status} onValueChange={(v) => setStatus(active.id, v)}>
                      <SelectTrigger data-testid="lead-status" className="bg-[#F4F4F5] border-black/10 rounded-none h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-black/10">{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {active.status === "converted" ? (
                    <span className="flex items-center gap-1 text-sm text-[#10B981] self-end pb-1"><CheckCircle2 className="h-4 w-4" /> Invited</span>
                  ) : (
                    <Button onClick={invite} disabled={inviting} data-testid="invite-client-btn" className="rounded-none bg-primary hover:bg-[#09090B] self-end">
                      {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><UserPlus className="h-4 w-4 mr-1" /> Invite as client</>}
                    </Button>
                  )}
                  <Button onClick={remove} variant="ghost" data-testid="lead-delete-btn" title="Delete lead" className="rounded-none self-end text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
